import React, { useState } from 'react';
import { Plus, Trash2, Gift, Target, Calendar, Percent } from 'lucide-react';
import InfoTooltip from '../../../../components/InfoTooltip';

const Step3CouponsIncentives = ({ formData, onChange }) => {
  const [activeTab, setActiveTab] = useState('coupons');

  const addCouponRule = () => {
    const newRule = {
      code: '',
      type: 'percentage',
      value: 0,
      maxDiscount: 0,
      minFare: 0,
      passengerField: '',
      condition: '',
      description: '',
      validFrom: new Date().toISOString().split('T')[0],
      validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    };

    onChange({
      dynamicCouponRules: [...(formData.dynamicCouponRules || []), newRule]
    });
  };

  const updateCouponRule = (index, updates) => {
    const newRules = [...(formData.dynamicCouponRules || [])];
    newRules[index] = { ...newRules[index], ...updates };
    onChange({ dynamicCouponRules: newRules });
  };

  const removeCouponRule = (index) => {
    const newRules = (formData.dynamicCouponRules || []).filter((_, i) => i !== index);
    onChange({ dynamicCouponRules: newRules });
  };

  const addDriverIncentive = () => {
    const newIncentive = {
      type: '',
      condition: '',
      bonus: 0
    };

    onChange({
      incentives: {
        ...formData.incentives,
        driverIncentives: [...(formData.incentives?.driverIncentives || []), newIncentive]
      }
    });
  };

  const updateDriverIncentive = (index, updates) => {
    const newIncentives = [...(formData.incentives?.driverIncentives || [])];
    newIncentives[index] = { ...newIncentives[index], ...updates };
    onChange({
      incentives: {
        ...formData.incentives,
        driverIncentives: newIncentives
      }
    });
  };

  const removeDriverIncentive = (index) => {
    const newIncentives = (formData.incentives?.driverIncentives || []).filter((_, i) => i !== index);
    onChange({
      incentives: {
        ...formData.incentives,
        driverIncentives: newIncentives
      }
    });
  };

  const addDriverPenalty = () => {
    const newPenalty = {
      type: '',
      condition: '',
      penalty: 0
    };

    onChange({
      incentives: {
        ...formData.incentives,
        driverPenalties: [...(formData.incentives?.driverPenalties || []), newPenalty]
      }
    });
  };

  const updateDriverPenalty = (index, updates) => {
    const newPenalties = [...(formData.incentives?.driverPenalties || [])];
    newPenalties[index] = { ...newPenalties[index], ...updates };
    onChange({
      incentives: {
        ...formData.incentives,
        driverPenalties: newPenalties
      }
    });
  };

  const removeDriverPenalty = (index) => {
    const newPenalties = (formData.incentives?.driverPenalties || []).filter((_, i) => i !== index);
    onChange({
      incentives: {
        ...formData.incentives,
        driverPenalties: newPenalties
      }
    });
  };

  const tabs = [
    { id: 'coupons', name: 'Dynamic Coupons', icon: Gift },
    { id: 'incentives', name: 'Driver Incentives', icon: Target },
    { id: 'penalties', name: 'Driver Penalties', icon: Target }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-2">Coupons & Incentives</h4>
        <p className="text-sm text-gray-600">
          Configure dynamic coupon rules and driver incentives/penalties to optimize pricing and driver behavior.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 inline mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Dynamic Coupons Tab */}
      {activeTab === 'coupons' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-medium text-gray-900">Dynamic Coupon Rules</h5>
            <button
              onClick={addCouponRule}
              className="btn btn-sm btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Coupon Rule
            </button>
          </div>

          <div className="space-y-4">
            {(formData.dynamicCouponRules || []).map((rule, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h6 className="text-sm font-medium text-gray-900">Coupon Rule #{index + 1}</h6>
                  <button
                    onClick={() => removeCouponRule(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">
                      Coupon Code
                      <InfoTooltip text="Unique code that passengers will use to apply this discount" />
                    </label>
                    <input
                      type="text"
                      value={rule.code}
                      onChange={(e) => updateCouponRule(index, { code: e.target.value })}
                      className="input"
                      placeholder="LOYALTY_BONUS"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Type
                      <InfoTooltip text="Discount type: percentage off, fixed amount off, or multiplier" />
                    </label>
                    <select
                      value={rule.type}
                      onChange={(e) => updateCouponRule(index, { type: e.target.value })}
                      className="input"
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                      <option value="multiplier">Multiplier</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">
                      Value
                      <InfoTooltip text="Discount value based on selected type" />
                    </label>
                    <input
                      type="number"
                      value={rule.value}
                      onChange={(e) => updateCouponRule(index, { value: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Max Discount (₹)
                      <InfoTooltip text="Maximum discount amount that can be applied with this coupon" />
                    </label>
                    <input
                      type="number"
                      value={rule.maxDiscount}
                      onChange={(e) => updateCouponRule(index, { maxDiscount: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Min Fare (₹)
                      <InfoTooltip text="Minimum fare required to use this coupon" />
                    </label>
                    <input
                      type="number"
                      value={rule.minFare}
                      onChange={(e) => updateCouponRule(index, { minFare: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Passenger Field
                      <InfoTooltip text="Passenger data field to check (e.g., stats.totalTrips)" />
                    </label>
                    <input
                      type="text"
                      value={rule.passengerField}
                      onChange={(e) => updateCouponRule(index, { passengerField: e.target.value })}
                      className="input"
                      placeholder="stats.totalTrips"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="form-label">
                      Condition
                      <InfoTooltip text="Condition to evaluate (e.g., stats.totalTrips >= 20)" />
                    </label>
                    <input
                      type="text"
                      value={rule.condition}
                      onChange={(e) => updateCouponRule(index, { condition: e.target.value })}
                      className="input"
                      placeholder="stats.totalTrips >= 20"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      value={rule.description}
                      onChange={(e) => updateCouponRule(index, { description: e.target.value })}
                      className="input"
                      placeholder="Loyalty bonus for frequent riders"
                    />
                  </div>
                  <div>
                    <label className="form-label">Valid From</label>
                    <input
                      type="date"
                      value={rule.validFrom}
                      onChange={(e) => updateCouponRule(index, { validFrom: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Valid To</label>
                    <input
                      type="date"
                      value={rule.validTo}
                      onChange={(e) => updateCouponRule(index, { validTo: e.target.value })}
                      className="input"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Driver Incentives Tab */}
      {activeTab === 'incentives' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-medium text-gray-900">Driver Incentives</h5>
            <button
              onClick={addDriverIncentive}
              className="btn btn-sm btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Incentive
            </button>
          </div>

          <div className="space-y-4">
            {(formData.incentives?.driverIncentives || []).map((incentive, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h6 className="text-sm font-medium text-gray-900">Incentive #{index + 1}</h6>
                  <button
                    onClick={() => removeDriverIncentive(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">
                      Type
                      <InfoTooltip text="Incentive trigger type (e.g., tripCountToday)" />
                    </label>
                    <input
                      type="text"
                      value={incentive.type}
                      onChange={(e) => updateDriverIncentive(index, { type: e.target.value })}
                      className="input"
                      placeholder="tripCountToday"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Condition
                      <InfoTooltip text="Condition to meet for incentive (e.g., >= 5)" />
                    </label>
                    <input
                      type="text"
                      value={incentive.condition}
                      onChange={(e) => updateDriverIncentive(index, { condition: e.target.value })}
                      className="input"
                      placeholder=">= 5"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Bonus (₹)
                      <InfoTooltip text="Bonus amount awarded when condition is met" />
                    </label>
                    <input
                      type="number"
                      value={incentive.bonus}
                      onChange={(e) => updateDriverIncentive(index, { bonus: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Driver Penalties Tab */}
      {activeTab === 'penalties' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-medium text-gray-900">Driver Penalties</h5>
            <button
              onClick={addDriverPenalty}
              className="btn btn-sm btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Penalty
            </button>
          </div>

          <div className="space-y-4">
            {(formData.incentives?.driverPenalties || []).map((penalty, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h6 className="text-sm font-medium text-gray-900">Penalty #{index + 1}</h6>
                  <button
                    onClick={() => removeDriverPenalty(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">
                      Type
                      <InfoTooltip text="Penalty trigger type (e.g., acceptance_rate)" />
                    </label>
                    <input
                      type="text"
                      value={penalty.type}
                      onChange={(e) => updateDriverPenalty(index, { type: e.target.value })}
                      className="input"
                      placeholder="acceptance_rate"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Condition
                      <InfoTooltip text="Condition that triggers penalty (e.g., < 70)" />
                    </label>
                    <input
                      type="text"
                      value={penalty.condition}
                      onChange={(e) => updateDriverPenalty(index, { condition: e.target.value })}
                      className="input"
                      placeholder="< 70"
                    />
                  </div>
                  <div>
                    <label className="form-label">
                      Penalty (₹)
                      <InfoTooltip text="Penalty amount deducted when condition is met" />
                    </label>
                    <input
                      type="number"
                      value={penalty.penalty}
                      onChange={(e) => updateDriverPenalty(index, { penalty: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="text-sm font-medium text-blue-800 mb-2">Configuration Summary</h5>
        <div className="text-sm text-blue-700 space-y-1">
          <p>Dynamic Coupon Rules: {(formData.dynamicCouponRules || []).length}</p>
          <p>Driver Incentives: {(formData.incentives?.driverIncentives || []).length}</p>
          <p>Driver Penalties: {(formData.incentives?.driverPenalties || []).length}</p>
        </div>
      </div>
    </div>
  );
};

export default Step3CouponsIncentives;
